import React, { ReactElement, useEffect, useRef, useState } from "react";
import portraits, { defaultPortrait } from "../assets";
import BulletArt from "../custom-alert/BulletArt";
import { GameState, LobbyState, Role } from "../types";
import { doesHitlerKnowFascists, isVictoryState } from "../utils";
import { getNextPresident, governmentFailed, isTermLimited } from "./nextPresident";
import "./GameScreen.css";

type PlayerCardsProps = {
  user: string;
  gameState: GameState;
  /** Reveal each player's last vote (animated one after another). */
  showVotes: boolean;
  /** The votes to reveal. They arrive with the new game state, which the board has not shown yet. */
  votes?: Record<string, boolean>;
  /** Show "thinking" dots on players the game is waiting for. */
  showBusy: boolean;
  /** "round" seats the players around an oval table with the current user at the bottom (the /GameB layout). */
  layout?: "grid" | "round";
};

/** Where seat number {@code index} of {@code count} sits on the table, as percentages of the table area. */
export function roundSeatPosition(
  index: number,
  count: number,
  firstIndex: number,
  radiusX = 43,
  radiusY = 38
): { x: number; y: number } {
  // The seat at firstIndex is at the bottom; the others follow clockwise in turn order.
  const angle = Math.PI / 2 + ((index - firstIndex) * 2 * Math.PI) / Math.max(1, count);
  return {
    x: Math.round((50 + radiusX * Math.cos(angle)) * 100) / 100,
    y: Math.round((50 + radiusY * Math.sin(angle)) * 100) / 100,
  };
}

/** Players the game is waiting on, as in PlayerDisplay. */
export function getBusyPlayers(game: GameState): Set<string> {
  const busy = new Set<string>();
  switch (game.state) {
    case LobbyState.CHANCELLOR_NOMINATION:
    case LobbyState.LEGISLATIVE_PRESIDENT:
    case LobbyState.LEGISLATIVE_PRESIDENT_VETO:
    case LobbyState.PP_PEEK:
    case LobbyState.PP_ELECTION:
    case LobbyState.PP_EXECUTION:
    case LobbyState.PP_INVESTIGATE:
    case LobbyState.POST_LEGISLATIVE:
      busy.add(game.president);
      break;
    case LobbyState.LEGISLATIVE_CHANCELLOR:
      busy.add(game.chancellor);
      break;
    case LobbyState.CHANCELLOR_VOTING:
      game.playerOrder.forEach((name) => {
        if (game.players[name].alive && !game.userVotes.hasOwnProperty(name)) {
          busy.add(name);
        }
      });
      break;
    default:
  }
  return busy;
}

/** Same visibility rules as PlayerDisplay: only reveal roles this user is allowed to know. */
function shouldShowRole(game: GameState, user: string, name: string): boolean {
  const myRole = game.players[user]?.id;
  const otherRole = game.players[name]?.id;
  if (otherRole === undefined) {
    return false;
  }
  if (isVictoryState(game.state)) {
    return true;
  }
  if (myRole === Role.FASCIST || (myRole === Role.HITLER && doesHitlerKnowFascists(game))) {
    return otherRole !== Role.LIBERAL; // Liberal labels would just be noise for Fascists.
  }
  return true;
}

type Tag = { text: string; kind: string };

function getTag(game: GameState, name: string): Tag | undefined {
  const player = game.players[name];
  if (!player.alive) {
    return { text: "Executed", kind: "dead" };
  }
  if (name === game.chancellor && !governmentFailed(game)) {
    return game.state === LobbyState.CHANCELLOR_VOTING
      ? { text: "Nominee", kind: "nominee" }
      : { text: "Chancellor", kind: "chancellor" };
  }
  if (name === game.president) {
    return { text: "President", kind: "president" };
  }
  if (game.state === LobbyState.CHANCELLOR_NOMINATION) {
    return isTermLimited(game, name)
      ? { text: "Term limited", kind: "limited" }
      : { text: "Eligible", kind: "eligible" };
  }
  return undefined;
}

/** A magnifying glass; the badge of a player whose party membership the president has already checked. */
function MagnifierIcon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
      <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth="2.6" />
      <line x1="14.6" y1="14.6" x2="20.5" y2="20.5" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
    </svg>
  );
}

const ROLE_LABEL: Record<string, string> = {
  [Role.LIBERAL]: "Liberal",
  [Role.FASCIST]: "Fascist",
  [Role.HITLER]: "Hitler",
};

/**
 * The table of players as cards: portrait, name, status tag, known role, and vote stamps.
 */
export default function PlayerCards(props: PlayerCardsProps): ReactElement {
  const game = props.gameState;
  const busy = getBusyPlayers(game);
  const nextPresident = isVictoryState(game.state) ? undefined : getNextPresident(game);

  // Reveal votes one card at a time, like the original vote animation.
  const [votesShown, setVotesShown] = useState(0);
  const timers = useRef<NodeJS.Timeout[]>([]);
  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setVotesShown(0);
    if (props.showVotes) {
      const step = 1000 / Math.max(1, game.playerOrder.length);
      timers.current = game.playerOrder.map((_n, i) =>
        setTimeout(() => setVotesShown(i + 1), (i + 1) * step)
      );
    }
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showVotes]);

  const round = props.layout === "round";
  const mySeat = Math.max(0, game.playerOrder.indexOf(props.user));

  return (
    <div className={"pc-grid" + (round ? " pc-round" : "")}>
      {game.playerOrder.map((name, index) => {
        const player = game.players[name];
        const tag = getTag(game, name);
        const role = player.id;
        const showRole = role !== undefined && shouldShowRole(game, props.user, name);
        const isBusy = props.showBusy && !props.showVotes && busy.has(name);
        const vote = (props.votes ?? game.userVotes)[name];
        const showVote = props.showVotes && index < votesShown && player.alive && vote !== undefined;
        const portrait = portraits[game.icon[name]] ?? portraits[defaultPortrait];
        // Only the President who investigated this player is told their party.
        const checked = player.investigated ? game.investigations?.[name] : undefined;

        let className = "pc-card";
        if (tag) className += " pc-" + tag.kind;
        const isNext = player.alive && name === nextPresident && name !== game.president;
        if (isNext) className += " pc-next";
        if (name === props.user) className += " pc-you";
        if (!player.alive) className += " pc-dead";
        if (player.investigated) className += " pc-investigated";

        const card = (
          <div className={className} key={round ? undefined : name}>
            <span className="pc-seat" aria-label={"Seat " + (index + 1)}>
              {index + 1}
            </span>
            {player.investigated && (
              <span
                className="pc-investigated-badge"
                title="Already investigated: the president knows this player's party"
                aria-label="Investigated"
              >
                <MagnifierIcon />
              </span>
            )}
            {isNext && <span className="pc-next-flag">Next president</span>}
            {isBusy && (
              <span className="pc-busy" aria-label="Thinking">
                <i />
                <i />
                <i />
              </span>
            )}
            <div className="pc-avatar-wrap">
              <div className="pc-avatar">
                {portrait ? <img src={portrait} alt="" /> : <span>{name.charAt(0)}</span>}
                {showVote && (
                  <span className={"pc-vote " + (vote ? "pc-vote-ja" : "pc-vote-nein")}>
                    {vote ? "JA!" : "NEIN!"}
                  </span>
                )}
              </div>
              {!player.alive && (
                <span className="pc-bullet" role="img" aria-label="Executed">
                  <BulletArt idPrefix={"pc-bullet-" + index} />
                </span>
              )}
            </div>
            <div className="pc-name">
              {name}
              {name === props.user && <span className="pc-you-label"> (you)</span>}
            </div>
            <div className="pc-tags">
              {tag && <span className={"pc-tag pc-tag-" + tag.kind}>{tag.text}</span>}
              {player.investigated && checked === undefined && (
                <span className="pc-tag pc-tag-investigated">Investigated</span>
              )}
              {checked !== undefined && (
                <span
                  className={
                    "pc-tag pc-tag-checked " + (checked === "FASCIST" ? "pc-tag-checked-fascist" : "pc-tag-checked-liberal")
                  }
                  title="What your investigation showed"
                >
                  Checked: {checked === "FASCIST" ? "Fascist" : "Liberal"}
                </span>
              )}
              {showRole && (
                <span className={"pc-tag pc-role-" + role!.toLowerCase()}>{ROLE_LABEL[role!]}</span>
              )}
            </div>
          </div>
        );
        if (!round) {
          return card;
        }
        const pos = roundSeatPosition(index, game.playerOrder.length, mySeat);
        return (
          <div
            className="pc-slot"
            key={name}
            style={{ "--x": pos.x + "%", "--y": pos.y + "%" } as React.CSSProperties}
          >
            {card}
          </div>
        );
      })}
    </div>
  );
}
